import React, { useState } from 'react';
import './PaymentModal.css';

const PaymentModal = ({ 
  isOpen, 
  onClose
}) => {
  const [selectedPackage, setSelectedPackage] = useState('solo');
  const [contactInfo, setContactInfo] = useState('');
  const [contactType, setContactType] = useState('phone');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const initiatePaymentUrl = `${apiBaseUrl}Payments/initiate`;

  const packages = {
    solo: { searches: 5, price: 3.00, label: 'Mini' },
    pro: { searches: 15, price: 7.00, label: 'Standard' },
    master: { searches: 40, price: 20.00, label: 'Maxi' }
  };

  function checkAndFormatNumber(phoneNumber) {
    const normalizedPhoneNumber = phoneNumber
      .split('(').join('')
      .split(')').join('')
      .split('-').join('')
      .split(' ').join('');

    if (/^00386\d{8}$/.test(normalizedPhoneNumber)) {
      return normalizedPhoneNumber;
    }

    if (/^\+386\d{8}$/.test(normalizedPhoneNumber)) {
      return normalizedPhoneNumber.replace('+', '00');
    }

    if (/^386\d{8}$/.test(normalizedPhoneNumber)) {
      return `00${normalizedPhoneNumber}`;
    }

    if (/^0\d{8}$/.test(normalizedPhoneNumber)) {
      return `00386${normalizedPhoneNumber.substring(1, 9)}`;
    }

    return 'invalid';
  }

  function validateAlias(alias) {
    if (alias === '') {
      return 'empty';
    }

    let testCase = '';
    let aliasError = false;

    if (alias.indexOf('@') === -1) {
      testCase = checkAndFormatNumber(alias);
    } else {
      aliasError = !alias.match(/^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/g);
    }

    if (testCase === 'invalid' || aliasError) {
      return 'invalid';
    }

    return 'valid';
  }

  const validateContactInfo = () => {
    const trimmedContactInfo = contactInfo.trim();
    const validationResult = validateAlias(trimmedContactInfo);

    if (validationResult === 'empty') {
      setError(`Please enter your ${contactType}`);
      return null;
    }

    if (validationResult === 'invalid') {
      setError(contactType === 'email' ? 'Please enter a valid email address' : 'Please enter a valid phone number');
      return null;
    }

    if (contactType === 'phone') {
      return checkAndFormatNumber(trimmedContactInfo);
    }

    return trimmedContactInfo;
  };

  function generateTransactionId() {
    const rand = crypto.randomUUID().replace(/-/g, "").substring(0, 6);

    const now = new Date();

    const pad = (n) => String(n).padStart(2, '0');

    const ts =
        now.getUTCFullYear().toString().slice(2) + // 2-digit year
        pad(now.getUTCMonth() + 1) +
        pad(now.getUTCDate()) + "-" +
        pad(now.getUTCHours()) +
        pad(now.getUTCMinutes()) +
        pad(now.getUTCSeconds());

    return `LASTNIK-${ts}-${rand}`;
  }

  const handlePayment = async () => {
    const normalizedContactInfo = validateContactInfo();
    if (!normalizedContactInfo) return;
    if (!selectedPackage) {
      setError('Please select a package');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {

      const paymentRequest = {
        transactionId: generateTransactionId(),
        packageId : selectedPackage,
        contactInfo: normalizedContactInfo,
        contactType,
        timestamp: Date.now()
      };

      sessionStorage.setItem('lastnik_payment_pending', JSON.stringify(paymentRequest));

      const response = await fetch(initiatePaymentUrl, {
        method: 'POST',
        headers: {
          'accept': 'text/plain',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentRequest),
        credentials: 'include'
      });

      const responseText = await response.text();
      const contentType = (response.headers.get('content-type') || '').toLowerCase();

      if (!response.ok) {
        let serverMessage = responseText;
        if (contentType.includes('application/json')) {
          try {
            const parsedError = JSON.parse(responseText);
            serverMessage = parsedError.message || parsedError.error || responseText;
          } catch {
            // Keep raw text fallback.
          }
        }
        throw new Error(serverMessage || 'Payment initiation failed');
      }

      let redirectUrl = '';
      if (contentType.includes('application/json')) {
        try {
          const parsed = JSON.parse(responseText);
          redirectUrl = parsed.redirectUrl || parsed.url || parsed.paymentUrl || '';
        } catch {
          // Fall back to plain text handling below.
        }
      }

      if (!redirectUrl) {
        redirectUrl = responseText.trim();
      }

      if (!redirectUrl || redirectUrl.startsWith('<!DOCTYPE') || redirectUrl.startsWith('<html')) {
        throw new Error('Payment API returned HTML instead of a payment URL. Check REACT_APP_PAYMENTS_INITIATE_URL and backend routing.');
      }

      if (!/^https?:\/\//i.test(redirectUrl)) {
        throw new Error('Payment API did not return a valid redirect URL.');
      }

      window.location.href = redirectUrl;
    } catch (err) {
      setError(`Napaka: ${err.message}`);
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const pkg = packages[selectedPackage];

  return (
    <div className="payment-overlay">
      <div className="payment-modal">
        {isProcessing ? (
          <div className="payment-loading">
            <div className="payment-spinner"></div>
            <p>Preusmeritev na plačilo...</p>
          </div>
        ) : (
          <>
            <div className="payment-header">
              <h2>Odklenite omejitve iskanja</h2>
              <p>Zmanjkuje vam brezplačnih iskanj</p>
            </div>

            <div className="payment-content">
              {error && <div className="payment-error">{error}</div>}

              <div className="contact-section">
                <div className="contact-label">FLIK KONTAKTNI PODATKI</div>
                <div className="contact-type-toggle">
                  <label className={`toggle-option ${contactType === 'phone' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="contactType"
                      value="phone"
                      checked={contactType === 'phone'}
                      onChange={(e) => {
                        setContactType(e.target.value);
                        setContactInfo('');
                        setError(null);
                      }}
                    />
                    <span>Mobilna številka</span>
                  </label>
                  <label className={`toggle-option ${contactType === 'email' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="contactType"
                      value="email"
                      checked={contactType === 'email'}
                      onChange={(e) => {
                        setContactType(e.target.value);
                        setContactInfo('');
                        setError(null);
                      }}
                    />
                    <span>Email</span>
                  </label>
                </div>
                <input
                  type={contactType === 'email' ? 'email' : 'tel'}
                  className="contact-input"
                  placeholder={contactType === 'phone' ? '051-234-567' : 'vas@email.com'}
                  value={contactInfo}
                  onChange={(e) => {
                    setContactInfo(e.target.value);
                    setError(null);
                  }}
                  disabled={isProcessing}
                />
              </div>

              <div className="payment-pricing">
                <div className="pricing-title">Izberite paket</div>
                <div className="pricing-options">
                  {Object.entries(packages).map(([key, value]) => (
                    <div
                      key={key}
                      className={`pricing-option ${selectedPackage === key ? 'selected' : ''}`}
                      onClick={() => setSelectedPackage(key)}
                    >
                      <span className="pricing-option-amount">€{value.price}</span>
                      <span className="pricing-option-desc">{value.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="payment-details">
                <div className="payment-detail-row">
                  <span className="payment-detail-label">Paket</span>
                  <span className="payment-detail-value">{pkg.label}</span>
                </div>
                <div className="payment-detail-row">
                  <span className="payment-detail-label">Vključenih iskanj</span>
                  <span className="payment-detail-value">{pkg.searches === Infinity ? 'Unlimited' : pkg.searches}</span>
                </div>
                <div className="payment-detail-row">
                  <span className="payment-detail-label">Cena</span>
                  <span className="payment-detail-value">€{pkg.price.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="payment-footer">
              <button
                className="btn-payment btn-pay"
                onClick={handlePayment}
                disabled={isProcessing || !contactInfo.trim()}
              >
                Plačilo s Flik → €{pkg.price.toFixed(2)}
              </button>
              <button
                className="btn-payment btn-cancel"
                onClick={onClose}
                disabled={isProcessing}
              >
                Prekliči
              </button>
            </div>

            <div className="payment-security">
              <span className="security-icon">🔒</span>
              Varno plačilo omogoča Flik
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
